import { ChevronUp, Package, Radio } from "lucide-react";
import { TranslatedText } from "@/components/common/text";
import { Spinner } from "@/components/common/svgs";

const countLabel = (count) => (count === 0 ? "No" : count);

const variantConfig = {
    empty: {
        iconWrapper: "bg-cm-p-surface-container",
        icon: <Package className="w-4 h-4 text-cm-p-text-secondary" />,
        titleClass: "text-cm-p-text-secondary font-medium !text-[14px]",
        subtitleClass: "text-cm-p-text-secondary",
        getTitle: () => "Select a truck to start seeing relevant loads",
        getSubtitle: () => null,
        toggleable: false,
    },
    all: {
        iconWrapper: "bg-cm-p-surface-container",
        icon: <Package className="w-4 h-4 text-cm-p-text-primary" />,
        titleClass: "text-cm-p-text-primary font-medium",
        subtitleClass: "text-cm-p-text-secondary",
        getTitle: (count) =>
            count === null ? "Loads matching current requirements" : `${countLabel(count)} loads matching current requirements`,
        getSubtitle: () => null,
        toggleable: true,
    },
    strategy: {
        iconWrapper: "bg-cm-p-surface-container",
        icon: <Package className="w-4 h-4 text-cm-p-text-primary" />,
        titleClass: "text-cm-p-text-primary font-medium",
        subtitleClass: "text-cm-p-text-secondary",
        getTitle: (count) => (count === null ? "loads" : `${countLabel(count)} loads`),
        getSubtitle: () => "based on strategy",
        toggleable: true,
    },
    active: {
        iconWrapper: "bg-cm-p-container-green",
        icon: <Package className="w-4 h-4 text-cm-p-on-container-green" />,
        titleClass: "text-cm-p-text-primary font-medium",
        subtitleClass: "text-cm-p-on-container-green font-medium",
        getTitle: (count) => (count === null ? "Matching loads" : `${countLabel(count)} matching loads`),
        getSubtitle: () => "for selected filter",
        toggleable: true,
    },
    "no-filter": {
        iconWrapper: "bg-cm-p-surface-container",
        icon: <Package className="w-4 h-4 text-cm-p-text-secondary" />,
        titleClass: "text-cm-p-text-primary font-medium",
        subtitleClass: "text-cm-p-text-secondary",
        getTitle: (count) => (count === null ? "Matching loads" : `${countLabel(count)} matching loads`),
        getSubtitle: () => "No active filters for this truck",
        toggleable: true,
    },
    filter: {
        iconWrapper: "bg-cm-p-container-green",
        icon: <Radio className="w-4 h-4 text-cm-p-on-container-green" />,
        titleClass: "text-cm-p-text-primary font-medium",
        subtitleClass: "text-cm-p-on-container-green",
        getTitle: (count) => `${count} ${count === 1 ? "filter" : "filters"}`,
        getSubtitle: (_, name) => (name ? `for ${name}` : null),
        toggleable: true,
    },
    other: {
        iconWrapper: "bg-cm-p-surface-container",
        icon: <Package className="w-4 h-4 text-cm-p-text-primary" />,
        titleClass: "text-cm-p-text-primary font-medium",
        subtitleClass: "text-cm-p-text-secondary",
        getTitle: (count) => {
            if (count === null) return "Other matching loads";
            return count === 0 ? "No other matching loads" : `Other ${count} matching loads`;
        },
        getSubtitle: () => null,
        toggleable: true,
    },
    requirements: {
        iconWrapper: "bg-cm-p-surface-container",
        icon: <Package className="w-4 h-4 text-cm-p-text-primary" />,
        titleClass: "text-cm-p-text-primary font-medium",
        subtitleClass: "text-cm-p-text-secondary",
        getTitle: (count) => (count === null ? "Loads matching requirements" : `${countLabel(count)} loads matching requirements`),
        getSubtitle: () => null,
        toggleable: true,
    },
};

export const LoadFinderTableHeader = ({ count, name, isExpanded, onToggle, variant = "all", loading, searching }) => {
    const config = variantConfig[variant] ?? variantConfig.all;
    const isToggleable = !searching && config.toggleable && typeof onToggle === "function";
    const title = searching ? "Searching loads" : config.getTitle(count, name);
    const subtitle = searching ? null : config.getSubtitle(count, name);

    return (
        <div
            className="w-full flex items-center gap-3 px-5 py-3 bg-cm-p-surface border-b border-cm-p-outline h-[63px]"
            onClick={isToggleable ? onToggle : undefined}
            style={{ cursor: isToggleable ? "pointer" : "default" }}
        >
            <div className={`w-7 h-7 rounded flex items-center justify-center shrink-0 ${config.iconWrapper}`}>
                {loading || searching ? <Spinner className="w-4 h-4 text-cm-p-accent" /> : config.icon}
            </div>

            <div className="flex items-center min-w-0 flex-1">
                <span className={`text-[20px] ${config.titleClass}`}>
                    <TranslatedText>{title}</TranslatedText>
                </span>
                {subtitle && (
                    <span className={`text-[14px] shrink-0 ml-2 ${config.subtitleClass}`}>
                        <TranslatedText>{subtitle}</TranslatedText>
                    </span>
                )}
                {isToggleable && (
                    <ChevronUp
                        className="w-4 h-4 text-cm-p-text-secondary shrink-0 transition-transform ml-3"
                        style={{ transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)" }}
                    />
                )}
            </div>
        </div>
    );
};
